/**
 * Client for the presigner endpoint (API Gateway HTTP API + Lambda).
 *
 * The SPA never connects to a fixed agent URL. It first requests a short-lived,
 * pre-signed WebSocket URL for a session, then opens the WS to AgentCore.
 */
export interface PresignResponse {
  /** Pre-signed, time-limited WebSocket URL to the AgentCore runtime. */
  wsUrl: string;
  /** Server echoes the session id it authorized. */
  sessionId: string;
}

const PRESIGNER_URL = import.meta.env.VITE_PRESIGNER_URL ?? '/api/presign';
/**
 * LOCAL DEV: bypass the presigner and connect straight to a local agent.
 * Set VITE_LOCAL_AGENT_WS=ws://localhost:8080 in voice/.env.local to use.
 */
const LOCAL_AGENT_WS = import.meta.env.VITE_LOCAL_AGENT_WS as string | undefined;

/**
 * Read the rejoin flag from the current page URL (`?rejoin=true`). The flag is
 * forwarded to the presigner so it can set a plain `rejoin` query param on the
 * signed URL (same survivability rule as `channel`).
 */
function readRejoinFromUrl(): boolean {
  if (typeof window === 'undefined') return false;
  return new URLSearchParams(window.location.search).get('rejoin') === 'true';
}

export async function getPresignedWsUrl(sessionId: string): Promise<PresignResponse> {
  const rejoin = readRejoinFromUrl();
  if (LOCAL_AGENT_WS) {
    const rejoinQuery = rejoin ? '&rejoin=true' : '';
    return {
      wsUrl: `${LOCAL_AGENT_WS}/?sessionId=${encodeURIComponent(sessionId)}${rejoinQuery}`,
      sessionId,
    };
  }
  // No credentials: the presigner authorizes via its own IAM role, and sending
  // credentials would forbid the wildcard CORS origin.
  // Include `rejoin` only when true so the body stays minimal otherwise.
  const res = await fetch(PRESIGNER_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(rejoin ? { sessionId, rejoin: true } : { sessionId }),
  });
  if (!res.ok) {
    throw new Error(`presigner ${res.status}: ${await res.text()}`);
  }
  return (await res.json()) as PresignResponse;
}
